import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { ModalHeader } from '@atlaskit/modal-dialog';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useLoomCrossJoinUrl } from '@confluence/loom-utils/entry-points/useLoomCrossJoinUrl.ts';

import {
	EXTERNAL_PRODUCT_CONNECTION,
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS,
	type ExternalProductConnection,
} from './types/RemoteObject.ts';

const i18n = defineMessages({
	title: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.title',
		defaultMessage: 'Connect apps for more suggestions',
		description: 'Title on the left panel of the third-party authentication modal',
	},
	description: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.description',
		defaultMessage: 'Connect at least one app to see curated suggestions here.',
		description: 'Description on the left panel of the third-party authentication modal',
	},
	loom: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.loom',
		defaultMessage: 'Loom',
		description: 'Label for the Loom auth option in the third-party authentication modal',
	},
	loomDescription: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.loom.description',
		defaultMessage: 'Surface videos, recordings and transcripts',
		description: 'Description for the Loom auth option in the third-party authentication modal',
	},
	microsoftTeams: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.microsoft-teams',
		defaultMessage: 'Microsoft Office',
		description:
			'Label for the Microsoft Office auth option in the third-party authentication modal',
	},
	microsoftTeamsDescription: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.microsoft-teams.description',
		defaultMessage: 'Bring in documents, presentations and files',
		description:
			'Description for the Microsoft Office auth option in the third-party authentication modal',
	},
	googleDrive: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.google-drive',
		defaultMessage: 'Google Drive',
		description: 'Label for the Google Drive auth option in the third-party authentication modal',
	},
	googleDriveDescription: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.google-drive.description',
		defaultMessage: 'Surface docs, sheets, slides and folders',
		description:
			'Description for the Google Drive auth option in the third-party authentication modal',
	},
	connect: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.connect',
		defaultMessage: 'Connect',
		description: 'Button label to connect a third-party app in the authentication modal',
	},
});

const OPTION_CONFIG: Record<
	ExternalProductConnection,
	{
		icon: string;
		labelMessage: (typeof i18n)[keyof typeof i18n];
		descriptionMessage: (typeof i18n)[keyof typeof i18n];
	}
> = {
	[EXTERNAL_PRODUCT_CONNECTION.LOOM]: {
		icon: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.LOOM],
		labelMessage: i18n.loom,
		descriptionMessage: i18n.loomDescription,
	},
	[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS]: {
		icon: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS],
		labelMessage: i18n.microsoftTeams,
		descriptionMessage: i18n.microsoftTeamsDescription,
	},
	[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: {
		icon: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE],
		labelMessage: i18n.googleDrive,
		descriptionMessage: i18n.googleDriveDescription,
	},
};

const styles = cssMap({
	layout: {
		height: '90vh',
		borderRadius: token('radius.xlarge'),
	},
	leftPanel: {
		flexGrow: 1,
		flexBasis: '50%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		overflowY: 'auto',
		paddingBlock: token('space.500'),
		paddingInline: token('space.400'),
	},
	optionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.200'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
	},
	optionIcon: {
		width: '24px',
		height: '24px',
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	optionLabel: {
		flexGrow: 1,
	},
	rightPanel: {
		flexGrow: 1,
		flexBasis: '50%',
		overflow: 'hidden',
		position: 'relative',
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#8fb8f6',
		borderStartEndRadius: token('radius.xlarge'),
		borderEndEndRadius: token('radius.xlarge'),
	},
	closeButtonWrapper: {
		top: token('space.100'),
		left: token('space.100'),
	},
});

type AuthOptionRowProps = {
	option: ExternalProductConnection;
	onConnect?: (option: ExternalProductConnection) => void;
};

const AuthOptionRow = ({ option, onConnect }: AuthOptionRowProps) => {
	const intl = useIntl();
	const { icon, labelMessage, descriptionMessage } = OPTION_CONFIG[option];
	const label = intl.formatMessage(labelMessage);
	const description = intl.formatMessage(descriptionMessage);

	return (
		<Box xcss={styles.optionRow} role="listitem">
			<Box xcss={styles.optionIcon}>
				<img src={icon} alt="" width={24} height={24} />
			</Box>
			<Box xcss={styles.optionLabel}>
				<Stack space="space.050">
					<Text weight="medium">{label}</Text>
					<Text size="small" color="color.text.subtle">
						{description}
					</Text>
				</Stack>
			</Box>
			<Button appearance="default" onClick={() => onConnect?.(option)}>
				{intl.formatMessage(i18n.connect)}
			</Button>
		</Box>
	);
};

type ModalLeftPanelProps = {
	authOptions: ExternalProductConnection[];
	outboundAuthUrls: Partial<Record<ExternalProductConnection, string>>;
};

const ModalLeftPanel = ({ authOptions, outboundAuthUrls }: ModalLeftPanelProps) => {
	const intl = useIntl();
	const loomCrossJoinUrl = useLoomCrossJoinUrl();

	const handleConnect = useCallback(
		async (option: ExternalProductConnection) => {
			switch (option) {
				case EXTERNAL_PRODUCT_CONNECTION.LOOM:
					window.open(loomCrossJoinUrl, '_blank', 'noreferrer');
					break;
				case EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS: {
					const authUrl = outboundAuthUrls[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS];
					if (authUrl) {
						await auth(authUrl);
					}
					break;
				}
				case EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE: {
					const authUrl = outboundAuthUrls[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE];
					if (authUrl) {
						await auth(authUrl);
					}
					break;
				}
				default:
					break;
			}
		},
		[loomCrossJoinUrl, outboundAuthUrls],
	);

	return (
		<Box xcss={styles.leftPanel}>
			<Stack space="space.200">
				<Heading size="medium">{intl.formatMessage(i18n.title)}</Heading>
				<Text color="color.text.subtle">{intl.formatMessage(i18n.description)}</Text>
			</Stack>
			<Stack space="space.150" role="list">
				{authOptions.map((option) => (
					<AuthOptionRow key={option} option={option} onConnect={handleConnect} />
				))}
			</Stack>
		</Box>
	);
};

const ModalRightPanel = () => {
	return (
		<Box xcss={styles.rightPanel}>
			<Box xcss={styles.closeButtonWrapper}>
				<ModalHeader hasCloseButton />
			</Box>
		</Box>
	);
};

type ThirdPartyAuthenticationModalProps = {
	authOptions: ExternalProductConnection[];
	outboundAuthUrls?: Partial<Record<ExternalProductConnection, string>>;
	onClose: () => void;
};

export const ThirdPartyAuthenticationModal = ({
	authOptions,
	outboundAuthUrls = {},
	onClose,
}: ThirdPartyAuthenticationModalProps) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width="90vw">
			<Flex xcss={styles.layout}>
				<ModalLeftPanel authOptions={authOptions} outboundAuthUrls={outboundAuthUrls} />
				<ModalRightPanel />
			</Flex>
		</Modal>
	);
};
